// src/components/MainTable/RowActions.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { EllipsisOutlined } from '@ant-design/icons';
import { Modal, Select, Dropdown, Input, type MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuth } from 'src/AuthContext';

import {
  addToCart,
  getRenderings,
  unsubmitTakeoff,
  duplicateProject,
  createDraftOrder,
  toggleProjectLock,
  triggerAutoRender,
  updateProjectOwner,
  deleteParentProject,
  deleteProject,
  updateSfId,
} from '../../services/projects';

import type { User } from '../../types/user';
import type { Project } from '../../types/project';
import TransferToAccountModal from './TransferToAccount';

interface RowActionsProps {
  project: Project;
  otherVersions?: Array<{ id: string; name: string }>;
  users: User[];
  onSetActiveVersion: (newProjectId: string) => void;
  onDeleteParentProjectLocally: (parentProjectId: string) => void;
  onDeleteVersion: (projectId: string) => void;
  onUpdateSalesforceId?: (parentProjectId: string, sfId: string) => void;
}

/** Constants */
const MENU_KEYS = {
  OWNER: 'owner',
  ASSIGN_OPP: 'assignOpportunity',
  LOCK: 'lock',
  DELETE: 'delete',
  DELETE_VERSION: 'deleteVersion',
  NEW_VERSION: 'newVersion',
  FULL_COPY: 'fullCopy',
  BLUEPRINT_COPY: 'blueprintCopy',
  FROM_SCRATCH: 'fromScratch',
  DUPLICATE: 'duplicate',
  ADD_TO_CART: 'addToCart',
  COPY_CART_LINK: 'copyCartLink',
  CREATE_DRAFT: 'createDraftOrder',
  UNSUBMIT_TAKEOFF: 'unsubmitTakeoff',
  FULLY_RESET_TAKEOFF: 'fullyResetTakeoff',
  GET_RENDERINGS: 'getRenderings',
  TRIGGER_AUTORENDER: 'triggerAutoRender',
  SET_ACTIVE_VERSION: 'setActiveVersion',
  TRANSFER_PROJECT_BETWEEN_ACCOUNTS: 'transferProjectBetweenAccounts',
} as const;

const OVERLAY_CLASS = 'row-actions-menu';
const DEV_ITEM_CLASS = 'dev-item';

/** Utils */
const isBlueprint = (t?: string) => (t || '').toLowerCase() === 'blueprint';
const isValidSalesforceId = (id: string) => id.length === 15 || id.length === 18;

/** Reusable async runner with toast */
async function run<T>(fn: () => Promise<T>, msgs: { ok: string; err: string }): Promise<T | null> {
  try {
    const res = await fn();
    toast.success(msgs.ok);
    return res;
  } catch {
    toast.error(msgs.err);
    return null;
  }
}

/** Component */
const RowActions: React.FC<RowActionsProps> = ({
  project,
  otherVersions = [],
  users,
  onSetActiveVersion,
  onDeleteParentProjectLocally,
  onDeleteVersion,
  onUpdateSalesforceId,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [modal, contextHolder] = Modal.useModal();

  const [locked, setLocked] = useState<boolean>(!!project.locked);
  const [ownerModalVisible, setOwnerModalVisible] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<string | null>(null);
  const [sfIdModalVisible, setSfIdModalVisible] = useState(false);
  const [salesforceId, setSalesforceId] = useState<string>('');
  const [transferModalVisible, setTransferModalVisible] = useState(false);

  useEffect(() => setLocked(!!project.locked), [project.locked]);

  /** Actions */
  const handleToggleLock = useCallback(async () => {
    const res = await run(() => toggleProjectLock(project.parentProjectId!), {
      ok: locked ? 'Unlocked versions' : 'Locked versions',
      err: 'Failed to toggle lock',
    });
    if (res !== null) setLocked((prev) => !prev);
  }, [project.parentProjectId, locked]);

  const handleDuplicate = useCallback(async (projectId: string) => {
    await run(() => duplicateProject(projectId), {
      ok: 'Project duplicated successfully',
      err: 'Failed to duplicate project',
    });
  }, []);

  const handleDeleteParent = useCallback(() => {
    modal.confirm({
      title: 'Delete Entire Project',
      getContainer: () => document.body,
      content: (
        <>
          Are you sure you want to permanently delete the entire project{' '}
          <b>&quot;{project.name}&quot;</b>?
          <br />
          This will remove all its versions and cannot be undone.
        </>
      ),
      okType: 'danger',
      onOk: async () => {
        const res = await run(() => deleteParentProject(project.parentProjectId!), {
          ok: 'Project permanently deleted',
          err: 'Failed to delete project',
        });
        if (res !== null) onDeleteParentProjectLocally(project.parentProjectId!);
      },
    });
  }, [project.name, project.parentProjectId, onDeleteParentProjectLocally]);
  const handleDeleteVersion = useCallback(() => {
    modal.confirm({
      title: 'Delete Active Version',
      getContainer: () => document.body,
      content: (
        <>
          Are you sure you want to permanently delete this version{' '}
          <b>
            &quot;{project.name} - {project.versionName}&quot;
          </b>
          ?
          <br />
          This cannot be undone.
        </>
      ),
      okType: 'danger',
      onOk: async () => {
        const res = await run(() => deleteProject(project.id!), {
          ok: 'Version permanently deleted',
          err: 'Failed to delete version',
        });
        if (res !== null) onDeleteVersion(project.id!);
      },
    });
  }, [project.name, project.id, onDeleteVersion]);
  const handleUnsubmitTakeoff = useCallback(
    async (fullyReset: boolean) => {
      if (!user.isDev && fullyReset === false) {
        toast.error('Only dev users can unsubmit takeoff');
        return;
      }
      await run(() => unsubmitTakeoff(project.id!, fullyReset), {
        ok: fullyReset ? 'Takeoff fully reset' : 'Takeoff unsubmitted',
        err: 'Failed to update takeoff',
      });
    },
    [project.id, user.isDev],
  );

  const handleGetRenderings = useCallback(async () => {
    const response = await run(() => getRenderings(project.id!), {
      ok: 'Rendering PDF ready',
      err: 'Failed to get rendering PDF',
    });
    if (response?.url) window.open(response.url, '_blank');
  }, [project.id]);

  const handleTriggerAutoRender = useCallback(async () => {
    await run(() => triggerAutoRender(project.id!), {
      ok: 'Auto-render triggered',
      err: 'Failed to trigger auto-render',
    });
  }, [project.id, user.isDev]);

  const handleAddToCart = useCallback(async () => {
    const link = await run(() => addToCart(project.id!), {
      ok: 'Project added to cart',
      err: 'Failed to add project to cart',
    });
    if (link) window.open(link, '_blank');
  }, [project.id]);

  const handleCopyCartLink = useCallback(async () => {
    const link = await run(() => addToCart(project.id!), {
      ok: 'Cart link copied',
      err: 'Failed to get cart link',
    });
    if (link) {
      await navigator.clipboard.writeText(link);
    }
  }, [project.id]);

  const handleCreateDraftOrder = useCallback(async () => {
    const link = await run(() => createDraftOrder(project.id!), {
      ok: 'Draft order created',
      err: 'Failed to create draft order',
    });
    if (link) window.open(link, '_blank');
  }, [project.id]);

  const goNewVersion = useCallback(
    (copyAction: 'full' | 'blueprint') => {
      navigate(
        `/new-version/${project.parentProjectId}?copyAction=${copyAction}&projectId=${project.id}`,
      );
    },
    [navigate, project.id, project.parentProjectId],
  );

  const goFromScratch = useCallback(() => {
    let url = `/add-project?parentProjectId=${project.parentProjectId}`;
    if (user.isRetailRep) {
      url += `&type=design&unitType=single&customerEmail=${project.customer}&sfId=${project.salesforceId}`;
    }
    if (user.firstName) navigate(url);
  }, [
    navigate,
    project.parentProjectId,
    project.customer,
    project.salesforceId,
    user.isRetailRep,
    user.firstName,
  ]);

  /** Owner modal handlers */
  const handleOwnerModalOk = useCallback(async () => {
    if (!selectedOwner) return;
    const res = await run(() => updateProjectOwner(project.parentProjectId!, selectedOwner), {
      ok: 'Owner updated',
      err: 'Failed to update owner',
    });
    if (res !== null) {
      setOwnerModalVisible(false);
      setSelectedOwner(null);
    }
  }, [project.parentProjectId, selectedOwner]);

  const handleOwnerModalCancel = useCallback(() => {
    setOwnerModalVisible(false);
    setSelectedOwner(null);
  }, []);

  /** Salesforce modal handlers */
  const handleSfIdModalOk = useCallback(async () => {
    if (!isValidSalesforceId(salesforceId)) {
      toast.error('Please enter a valid Salesforce ID (15 or 18 characters)');
      return;
    }
    const res = await run(() => updateSfId(project.parentProjectId!, salesforceId), {
      ok: 'Salesforce ID assigned successfully',
      err: 'Failed to assign Salesforce ID',
    });
    if (res !== null) {
      setSfIdModalVisible(false);
      setSalesforceId('');
      onUpdateSalesforceId?.(project.parentProjectId!, salesforceId);
    }
  }, [project.parentProjectId, salesforceId, onUpdateSalesforceId]);

  const handleSfIdModalCancel = useCallback(() => {
    setSfIdModalVisible(false);
    setSalesforceId('');
  }, []);

  /** Menu building */
  const duplicateChildren = useMemo<MenuProps['items']>(() => {
    const current = {
      label: `${project.versionName || 'Unnamed Version'} (Current)`,
      key: `${MENU_KEYS.DUPLICATE}-${project.id}`,
    };
    const others = otherVersions.map((v) => ({
      label: v.name,
      key: `${MENU_KEYS.DUPLICATE}-${v.id}`,
    }));
    return [current, ...others];
  }, [project.versionName, project.id, otherVersions]);

  const versionChildren = useMemo<MenuProps['items']>(
    () => otherVersions.map((v) => ({ label: v.name, key: `version-${v.id}` })),
    [otherVersions],
  );

  const devExtras: MenuProps['items'] = useMemo(() => {
    if (!user.isDev) return [];
    return [
      { label: 'Get Project Renderings', key: MENU_KEYS.GET_RENDERINGS, className: DEV_ITEM_CLASS },
    ];
  }, [user.isDev]);

  const extras: MenuProps['items'] = useMemo(() => {
    if (!user.canTransferProjects) return [];
    return [
      {
        label: 'Transfer to Another Account',
        key: MENU_KEYS.TRANSFER_PROJECT_BETWEEN_ACCOUNTS,
        className: DEV_ITEM_CLASS,
      },
    ];
  }, [user.canTransferProjects]);

  const cartExtras: MenuProps['items'] = useMemo(() => {
    if (user.isMultifamily) return [];
    if (!user.isDev) return [];
    if (!project.closetsQty || project.closetsQty <= 0) return [];
    return [
      { label: 'Add To Cart', key: MENU_KEYS.ADD_TO_CART },
      { label: 'Copy Cart Link', key: MENU_KEYS.COPY_CART_LINK },
      { label: 'Create Draft Order', key: MENU_KEYS.CREATE_DRAFT },
    ];
  }, [user.isDev, user.isMultifamily, project.closetsQty]);

  const mfExtras: MenuProps['items'] = useMemo(() => {
    if (!user.isMultifamily) return [];
    return [
      {
        label: 'Trigger Auto-Render',
        key: MENU_KEYS.TRIGGER_AUTORENDER,
      },
    ];
  }, [user.isMultifamily]);

  const blueprintExtras: MenuProps['items'] = useMemo(() => {
    if (!isBlueprint(project.projectType)) return [];
    if (!user.isDev) return [];
    return [
      {
        label: 'Unsubmit Takeoff',
        key: MENU_KEYS.UNSUBMIT_TAKEOFF,
        className: DEV_ITEM_CLASS,
      },
      {
        label: 'Fully Reset Takeoff',
        key: MENU_KEYS.FULLY_RESET_TAKEOFF,
        className: DEV_ITEM_CLASS,
      },
    ];
  }, [project.projectType]);

  const baseItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      { label: 'Change Owner', key: MENU_KEYS.OWNER },
      ...(!project.salesforceId
        ? [{ label: 'Assign to Opportunity', key: MENU_KEYS.ASSIGN_OPP }]
        : []),
      { label: locked ? 'Unlock' : 'Lock', key: MENU_KEYS.LOCK },
      { label: 'Delete Project', key: MENU_KEYS.DELETE, danger: true },
      { label: 'Delete Active Version', key: MENU_KEYS.DELETE_VERSION, danger: true },
      {
        label: 'New Version',
        key: MENU_KEYS.NEW_VERSION,
        children: [
          { label: 'Full Copy', key: MENU_KEYS.FULL_COPY },
          { label: 'Blueprint Copy', key: MENU_KEYS.BLUEPRINT_COPY },
          { label: 'From Scratch', key: MENU_KEYS.FROM_SCRATCH },
        ],
      },
      ...devExtras,
      ...extras,
      {
        label: 'Duplicate',
        key: MENU_KEYS.DUPLICATE,
        children: duplicateChildren,
      },
      ...mfExtras,
      ...cartExtras,
      ...blueprintExtras,
    ];

    if (otherVersions.length > 0) {
      items.push({
        label: 'Set Active Version',
        key: MENU_KEYS.SET_ACTIVE_VERSION,
        children: versionChildren,
      });
    }
    return items;
  }, [
    locked,
    project.salesforceId,
    devExtras,
    duplicateChildren,
    mfExtras,
    cartExtras,
    blueprintExtras,
    otherVersions.length,
    versionChildren,
  ]);

  /** Menu click router */
  const onMenuClick: MenuProps['onClick'] = (info) => {
    const { key } = info;

    if (key.startsWith('version-')) {
      onSetActiveVersion(key.replace('version-', ''));
      return;
    }
    if (key.startsWith(`${MENU_KEYS.DUPLICATE}-`)) {
      handleDuplicate(key.replace(`${MENU_KEYS.DUPLICATE}-`, ''));
      return;
    }

    switch (key) {
      case MENU_KEYS.OWNER:
        setOwnerModalVisible(true);
        break;
      case MENU_KEYS.ASSIGN_OPP:
        setSfIdModalVisible(true);
        break;
      case MENU_KEYS.LOCK:
        handleToggleLock();
        break;
      case MENU_KEYS.DELETE:
        handleDeleteParent();
        break;
      case MENU_KEYS.DELETE_VERSION:
        handleDeleteVersion();
        break;
      case MENU_KEYS.UNSUBMIT_TAKEOFF:
        handleUnsubmitTakeoff(false);
        break;
      case MENU_KEYS.FULLY_RESET_TAKEOFF:
        handleUnsubmitTakeoff(true);
        break;
      case MENU_KEYS.GET_RENDERINGS:
        handleGetRenderings();
        break;
      case MENU_KEYS.TRIGGER_AUTORENDER:
        handleTriggerAutoRender();
        break;
      case MENU_KEYS.FULL_COPY:
        goNewVersion('full');
        break;
      case MENU_KEYS.BLUEPRINT_COPY:
        goNewVersion('blueprint');
        break;
      case MENU_KEYS.FROM_SCRATCH:
        goFromScratch();
        break;
      case MENU_KEYS.DUPLICATE:
        handleDuplicate(project.id!);
        break;
      case MENU_KEYS.ADD_TO_CART:
        handleAddToCart();
        break;
      case MENU_KEYS.COPY_CART_LINK:
        handleCopyCartLink();
        break;
      case MENU_KEYS.CREATE_DRAFT:
        handleCreateDraftOrder();
        break;
      case MENU_KEYS.TRANSFER_PROJECT_BETWEEN_ACCOUNTS:
        setTransferModalVisible(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
      {contextHolder}
      <style>
        {`
          .${OVERLAY_CLASS} .ant-dropdown-menu-item:hover,
          .${OVERLAY_CLASS} .ant-dropdown-menu-submenu-title:hover {
            background: #f5faff;
          }
          .${OVERLAY_CLASS} .${DEV_ITEM_CLASS} {
            background: #fff9db;
          }
          .${OVERLAY_CLASS} .${DEV_ITEM_CLASS}:hover {
            background: #fff3bf;
          }
        `}
      </style>

      <Modal
        title='Change Project Ownership'
        open={ownerModalVisible}
        onOk={handleOwnerModalOk}
        onCancel={handleOwnerModalCancel}
        okButtonProps={{ disabled: !selectedOwner }}
      >
        <Select
          style={{ width: '100%' }}
          placeholder='Select new owner'
          value={selectedOwner}
          onChange={setSelectedOwner}
          options={users.map((u) => ({
            label: `${u.firstName} ${u.lastName}`,
            value: u.email,
          }))}
          showSearch
          optionFilterProp='label'
        />
      </Modal>

      <Modal
        title='Assign to Salesforce Opportunity'
        open={sfIdModalVisible}
        onOk={handleSfIdModalOk}
        onCancel={handleSfIdModalCancel}
        okButtonProps={{ disabled: !isValidSalesforceId(salesforceId) }}
      >
        <Input
          placeholder='Salesforce ID (15 or 18 characters)'
          value={salesforceId}
          onChange={(e) => setSalesforceId(e.target.value.trim())}
          minLength={15}
          status={salesforceId && !isValidSalesforceId(salesforceId) ? 'error' : ''}
        />
        {salesforceId && !isValidSalesforceId(salesforceId) && (
          <div style={{ color: '#ff4d4f', marginTop: 4, fontSize: 12 }}>
            Salesforce ID must be 15 or 18 characters (currently {salesforceId.length})
          </div>
        )}
      </Modal>

      <Dropdown
        menu={{ items: baseItems, onClick: onMenuClick }}
        trigger={['click']}
        overlayClassName={OVERLAY_CLASS}
      >
        <EllipsisOutlined style={{ fontSize: 16, cursor: 'pointer' }} />
      </Dropdown>
      <TransferToAccountModal
        open={transferModalVisible}
        onClose={() => setTransferModalVisible(false)}
        parentProjectId={project.parentProjectId!}
        onDeleteParentProjectLocally={onDeleteParentProjectLocally}
      />
    </>
  );
};

export default RowActions;